import { useEffect, useState } from "react";
import { CheckCircle2, Mail, Phone, X } from "lucide-react";
import { api } from "@/lib/api";

const inputClass = "w-full rounded-xl border border-[#826751]/25 bg-white px-3 py-3 text-sm outline-none focus:border-[#705744] focus-visible:ring-2 focus-visible:ring-[#826751]/20";
const buttonClass = "inline-flex min-h-[44px] items-center justify-center rounded-xl border border-[#826751]/25 px-4 text-sm text-[#4A382B] hover:bg-[#F4EADD]";

export default function ProfessionalMapContactDialog({ cardId, profile, mode, onClose, previewOnly = false }) {
  const [channel, setChannel] = useState("email");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [service, setService] = useState("");
  const [quoteRequest, setQuoteRequest] = useState("");
  const [quoteDate, setQuoteDate] = useState("");
  const [quoteCity, setQuoteCity] = useState("");
  const [quoteBudget, setQuoteBudget] = useState("");
  const [when, setWhen] = useState("any");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  // Existing private inbox accepts kind "quote" and one bounded message.
  const quoteMessage = [
    quoteRequest.trim() ? "Запрос: " + quoteRequest.trim() : "",
    quoteDate ? "Дата: " + quoteDate : "",
    quoteCity.trim() ? "Город / формат: " + quoteCity.trim() : "",
    quoteBudget.trim() ? "Бюджет: " + quoteBudget.trim() : "",
    message.trim() ? "Пожелания: " + message.trim() : "",
  ].filter(Boolean).join("\n");
  const submittedMessage = mode === "quote" ? quoteMessage : message.trim();
  const messageValid = mode === "quote"
    ? quoteRequest.trim().length >= 3 && quoteMessage.length <= 600
    : Boolean(message.trim());
  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  async function submit(event) {
    event.preventDefault();
    if (previewOnly || !consent || saving || !contact.trim() || !messageValid) return;
    setSaving(true);
    setError("");
    try {
      await api.post("/professional-map/profiles/" + encodeURIComponent(cardId) + "/contact-requests", {
        kind: mode, channel, contact: contact.trim(), message: submittedMessage,
        service: mode === "quote" ? service : "",
        call_when: channel === "phone" ? when : null, consent: true, website: honeypot,
      });
      setSent(true);
      setContact("");
      setMessage("");
      setQuoteRequest("");
      setQuoteDate("");
      setQuoteCity("");
      setQuoteBudget("");
    } catch (err) {
      const detail = JSON.stringify(err?.response?.data?.detail || "");
      if (detail.includes("medical_information_not_accepted")) {
        setError("Не указывай диагнозы, аллергии и медицинские сведения. Можно описать пожелания к составу: например, «без шоколада».");
      } else if (err?.response?.status === 429) {
        setError("Слишком много обращений. Попробуй позднее.");
      } else if (err?.response?.status === 404) {
        setError("Эта профессиональная страница сейчас недоступна.");
      } else {
        setError("Не удалось передать заявку. Проверь поля и попробуй ещё раз.");
      }
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-black/60 p-3 sm:p-6"
      data-testid="pm-contact-dialog" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section role="dialog" aria-modal="true" aria-labelledby="pm-contact-title"
        className="relative my-auto w-full max-w-[520px] rounded-[24px] bg-[#FFFCF8] p-5 text-[#3A2B22] shadow-xl sm:p-7">
        <button type="button" onClick={onClose} aria-label="Закрыть окно"
          className="absolute right-4 top-4 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl hover:bg-[#F3E7D9]">
          <X size={19} aria-hidden="true"/>
        </button>
        <h2 id="pm-contact-title" className="pr-12 font-serif text-2xl">
          {mode === "quote" ? "Запросить предложение" : "Связаться"}
        </h2>
        <p className="mt-2 text-sm text-[#725B4B]">
          {previewOnly
            ? "Это предпросмотр. Никакие заявки из черновика не отправляются."
            : mode === "quote"
              ? "Опиши задачу и удобный способ ответа. Заявку увидит только выбранный специалист."
              : <>Сообщение получит только {profile?.public_name || "выбранный специалист"} в своём кабинете.</>}
        </p>
        {sent ? <div role="status" className="mt-7 space-y-3" data-testid="pm-contact-sent">
          <CheckCircle2 size={27} className="text-[#51765B]" aria-hidden="true"/>
          <p className="font-medium">Запрос отправлен специалисту.</p>
          <p className="text-sm text-[#725B4B]">Он увидит обращение в кабинете и сможет связаться с тобой по указанному контакту.</p>
          <button type="button" onClick={onClose} className={buttonClass}>Закрыть</button>
        </div> :
        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="pm-contact-form">
          {mode === "quote" && (profile?.services || []).length > 0 && <label className="block space-y-1 text-sm">
            <span>Какая услуга интересует?</span>
            <select className={inputClass} value={service} onChange={(e) => setService(e.target.value)}
              data-testid="pm-contact-service">
              <option value="">Не выбрана / другой запрос</option>
              {profile.services.map(item => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>}
          {mode === "quote" && <div className="space-y-3 rounded-2xl border border-[#826751]/15 bg-[#F8F2EA] p-4"
            data-testid="pm-quote-details">
            <p className="text-sm font-medium text-[#4A382B]">Расскажи, какое предложение тебе нужно</p>
            <label className="block space-y-1 text-sm">
              <span>Что нужно заказать или обсудить? *</span>
              <textarea required rows={3} minLength={3} maxLength={280}
                className={inputClass} value={quoteRequest} onChange={(e) => setQuoteRequest(e.target.value)}
                placeholder="Например: торт на праздник, фотосъёмка или оформление десертного стола"
                data-testid="pm-quote-description"/>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1 text-sm">
                <span>Желаемая дата (необязательно)</span>
                <input type="date" className={inputClass} value={quoteDate}
                  onChange={(e) => setQuoteDate(e.target.value)} data-testid="pm-quote-date"/>
              </label>
              <label className="block space-y-1 text-sm">
                <span>Город или формат работы (необязательно)</span>
                <input type="text" maxLength={80} className={inputClass} value={quoteCity}
                  onChange={(e) => setQuoteCity(e.target.value)}
                  placeholder="Например: Вильнюс, онлайн" data-testid="pm-quote-location"/>
              </label>
            </div>
            <label className="block space-y-1 text-sm">
              <span>Ориентир по бюджету (необязательно)</span>
              <input type="text" maxLength={55} className={inputClass}
                value={quoteBudget} onChange={(e) => setQuoteBudget(e.target.value)}
                placeholder="Можно оставить пустым" data-testid="pm-quote-budget"/>
            </label>
          </div>}
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Как специалисту ответить?</legend>
            <div className="grid grid-cols-2 gap-2">
              <label className={buttonClass + (channel === "email" ? " border-[#6E533E] bg-[#F1E6D7]" : "")}>
                <input type="radio" name="channel" value="email" checked={channel === "email"}
                  onChange={() => { setChannel("email"); setContact(""); }} className="mr-2" />
                <Mail size={16} className="mr-1"/> Email
              </label>
              <label className={buttonClass + (channel === "phone" ? " border-[#6E533E] bg-[#F1E6D7]" : "")}>
                <input type="radio" name="channel" value="phone" checked={channel === "phone"}
                  onChange={() => { setChannel("phone"); setContact(""); }} className="mr-2"/>
                <Phone size={16} className="mr-1"/> Обратный звонок
              </label>
            </div>
          </fieldset>
          <label className="block space-y-1 text-sm">
            <span>{channel === "email" ? "Твой email" : "Твой номер телефона"}</span>
            <input required type={channel === "email" ? "email" : "tel"} className={inputClass}
              autoComplete={channel === "email" ? "email" : "tel"} maxLength={254}
              value={contact} onChange={(e) => setContact(e.target.value)}
              placeholder={channel === "email" ? "name@example.com" : "+370 ..."} />
          </label>
          {channel === "phone" && <label className="block space-y-1 text-sm">
            <span>Когда удобно позвонить?</span>
            <select className={inputClass} value={when} onChange={(e) => setWhen(e.target.value)}>
              <option value="any">В любое время</option>
              <option value="evening">Вечером</option>
              <option value="weekend">В выходные</option>
            </select>
          </label>}
          <label className="block space-y-1 text-sm">
            <span>{mode === "quote" ? "Дополнительные пожелания (необязательно)" : "Твоё сообщение"}</span>
            <textarea required={mode !== "quote"} rows={3} minLength={mode === "quote" ? undefined : 1}
              maxLength={mode === "quote" ? 120 : 600}
              className={inputClass} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder={mode === "quote" ? "Например: стиль, количество и важные детали" : "Напиши свой вопрос специалисту"}/>
          </label>
          {mode === "quote" && quoteMessage.length > 600 &&
            <p className="text-sm text-red-700" role="alert">Сократи описание заказа: вместе все поля должны содержать не более 600 символов.</p>}
          <p className="text-xs leading-5 text-[#786251]">
            Не указывай аллергии, диагнозы и медицинские сведения. Пожелания к ингредиентам можно описать без объяснения причины.
          </p>
          <div className="absolute left-[-9999px]" aria-hidden="true">
            <label>Не заполняй <input type="text" tabIndex={-1} autoComplete="off"
              value={honeypot} onChange={(e) => setHoneypot(e.target.value)}/></label>
          </div>
          <label className="flex items-start gap-2 text-xs leading-5 text-[#6A5546]">
            <input required type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
              className="mt-1"/>
            Я согласен на передачу указанных контактов и сообщения выбранному специалисту для ответа.
            Обращение доступно только выбранному специалисту.
          </label>
          {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
          <button type="submit" disabled={previewOnly || saving || !consent || !contact.trim() || !messageValid}
            className={"inline-flex min-h-[44px] w-full items-center justify-center rounded-xl px-4 font-medium disabled:opacity-50 " +
              (mode === "quote" ? "border border-[#826751]/25 bg-[#F4EADD] text-[#4A382B] hover:bg-[#EADCC9]" : "bg-[#644D3E] text-white")}
            data-testid="pm-contact-submit">{previewOnly ? "Отправка доступна после публикации" : saving ? "Отправляем…" : "Отправить запрос"}</button>
        </form>}
      </section>
    </div>
  );
}
